// PROTOTYPE — visual automation builder (React Flow canvas).
//
// FRONT-END ONLY. In-memory state, stub catalog, simulated Arbox key test.
// Nothing here calls the server or touches the real automation system. This is
// the UX prototype for the planned graph-engine redesign (see the architecture
// doc): a trigger start-node → one or more action bricks, each with field mapping.
//
// Lives at /app/automations-flow, alongside the existing /app/automations page
// which is left untouched.

import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Background,
    Controls,
    ReactFlow,
    ReactFlowProvider,
    addEdge,
    useEdgesState,
    useNodesState,
    useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {Box, Button, Typography} from "@mui/material";
import {BoltOutlined as BoltIcon, ArrowForward as BackIcon, SaveOutlined as SaveIcon, ScienceOutlined as TestIcon} from "@mui/icons-material";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useMessageContext} from "../contexts/MessageContext";
import {RTL} from "../cache/CacheRtl";
import {borders, colors} from "../componenets/ui/colors";
import {nodeTypes, BRAND} from "../componenets/automations/flow/FlowNodes";
import NodeConfigPanel from "../componenets/automations/flow/NodeConfigPanel";
import BrickPalette, {DRAG_MIME} from "../componenets/automations/flow/BrickPalette";
import RemovableEdge from "../componenets/automations/flow/RemovableEdge";
import TestRunDrawer from "../componenets/automations/flow/TestRunDrawer";
import {ACTION_CATALOG, DATA_CATALOG, actionByKind, triggerByKind} from "../componenets/automations/flow/nodeCatalog";
import {templateById} from "../componenets/automations/flow/mockAutomations";
import {loadCatalog} from "../componenets/automations/flow/catalogStore";
import {serializeFlow, deserializeFlow} from "../componenets/automations/flow/flowSerializer";
import {sampleTriggerData, adaptExecutionToDrawer} from "../componenets/automations/flow/testRunAdapter";
import {createFlow, updateFlow, getFlow, testRunFlow} from "../services/AutomationFlowService";
import {useCompany} from "../contexts/CompanyContext";
import {useAuth} from "../contexts/AuthContext";

const edgeTypes = {removable: RemovableEdge};

let idSeq = 1;
const nextId = () => `n${idSeq++}`;

// Flow reads left → right: a static Start node, then the trigger, then actions.
const initialNodes = [
    {id: "start", type: "start", position: {x: 60, y: 180}, data: {}, deletable: false, draggable: false},
    {id: "trigger", type: "trigger", position: {x: 260, y: 180}, data: {}},
];

// The line comes out of Start into the trigger.
const initialEdges = [
    {id: "e-start-trigger", source: "start", target: "trigger", animated: true, style: {stroke: BRAND}},
];

// Resolve a palette drag token ("nodeType:kind[:eventType]") to node data.
function resolveToken(token) {
    const [nodeType, kind, eventType] = token.split(":");
    if (nodeType === "trigger") {
        const trigger = triggerByKind(kind, eventType);
        return {nodeType, data: {trigger}};
    }
    if (nodeType === "data") {
        const brick = DATA_CATALOG.find(d => d.kind === kind);
        return {nodeType, data: {brick, mapping: {}}};
    }
    const action = ACTION_CATALOG.find(a => a.kind === kind);
    return {nodeType, data: {action, mapping: {}}};
}

// Build a seeded {nodes, edges} from a template's flow definition:
// Start → configured trigger → chained action bricks (sequence).
function buildFromTemplate(tmpl) {
    if (!tmpl?.flow) return {nodes: initialNodes, edges: initialEdges};

    const trigger = triggerByKind(tmpl.flow.triggerKind, tmpl.flow.eventType);
    const nodes = [
        {id: "start", type: "start", position: {x: 60, y: 180}, data: {}, deletable: false, draggable: false},
        {id: "trigger", type: "trigger", position: {x: 260, y: 180}, data: {trigger, keyOk: false}},
    ];
    const edges = [
        {id: "e-start-trigger", source: "start", target: "trigger", animated: true, style: {stroke: BRAND}},
    ];

    // A template action can be a bare kind string, or {kind, mapping, options, id}, or a
    // {dataTransform:{outputName, expression}, id}. `id` is the template-local reference name; we map it
    // to the built node's real output prefix so pre-filled {{id.field}} tokens resolve.
    const localToPrefix = {};   // template-local id → real output prefix (e.g. "createTrainee" → "create_trainee_ld3")

    const addNode = (spec, parentId, x, y) => {
        const id = nextId();
        let node;
        if (spec.dataTransform) {
            const brick = DATA_CATALOG.find(b => b.kind === "DATA_TRANSFORM");
            node = {id, type: "data", position: {x, y}, data: {brick, mapping: {}, formula: {...spec.dataTransform}}};
        } else {
            const action = ACTION_CATALOG.find(a => a.kind === spec.kind);
            node = {id, type: "action", position: {x, y},
                    data: {action, mapping: {...(spec.mapping || {})}, options: {...(spec.options || {})}}};
        }
        nodes.push(node);
        edges.push({id: `e-${parentId}-${id}`, source: parentId, target: id, animated: true, style: {stroke: BRAND}});
        if (spec.id) localToPrefix[spec.id] = prefixFor(node);
        return id;
    };

    const norm = (entry) => (typeof entry === "string" ? {kind: entry} : entry);

    let prevId = "trigger";
    let x = 480;
    (tmpl.flow.actions || []).forEach((entry) => {
        if (Array.isArray(entry)) {
            // Parallel fan-out off prevId — stack vertically so branches don't overlap.
            const yStart = 180 - ((entry.length - 1) * 120) / 2;
            entry.forEach((e, i) => addNode(norm(e), prevId, x, yStart + i * 120));
            x += 240;
        } else {
            prevId = addNode(norm(entry), prevId, x, 180);
            x += 240;
        }
    });

    // Resolve template-local ids in FIELD mappings → the real node output prefix. Trigger namespaces
    // (arbox.* / trainee.*) and already-real prefixes pass through unchanged.
    nodes.forEach(n => {
        const mapping = n.data?.mapping;
        if (!mapping) return;
        Object.values(mapping).forEach(m => {
            if (m?.type === "FIELD" && typeof m.value === "string") {
                const dot = m.value.indexOf(".");
                if (dot > 0) {
                    const head = m.value.slice(0, dot);
                    if (localToPrefix[head]) m.value = localToPrefix[head] + m.value.slice(dot);
                }
            }
        });
    });

    return {nodes, edges};
}

// A stable path prefix for a node's outputs (so {{createTrainee.traineeId}} is
// unambiguous even with two trainee bricks — the node id disambiguates).
function prefixFor(node) {
    if (node.type === "trigger") return node.data?.trigger?.source === "ARBOX" ? "arbox" : "trigger";
    const kind = node.data?.action?.kind || node.data?.brick?.kind || "step";
    return `${kind.toLowerCase()}_${node.id}`;
}

// A {{...}} token allows only word chars + dots ([\w.] server-side). A user-named formula output like
// "full name" (with a space) would produce an UNRESOLVABLE token, so we slugify the KEY (spaces →
// underscores) while keeping the human label as typed. Must match the server's slug in DataTransformBrick.
export function slugKey(name) {
    return (name || "").trim().replace(/\s+/g, "_");
}

// Human label + output field list for a node, for the "map from" grouping.
// Covers triggers, action bricks, AND data-transform bricks (data.brick), so a
// transform's `result` is offered to downstream steps.
function describeNode(node) {
    if (node.type === "trigger") {
        const t = node.data?.trigger;
        return {label: t ? `מ-${t.label}` : "מהטריגר", outputs: t?.outputs ?? []};
    }
    const spec = node.data?.action || node.data?.brick;
    if (spec) {
        // Formula brick advertises its USER-NAMED output (data.formula.outputName),
        // falling back to "result" until named.
        if (spec.formula) {
            // The KEY (used in {{data_n.key}}) is slugified so a name with spaces still resolves; the
            // LABEL keeps what the user typed.
            const raw = node.data?.formula?.outputName?.trim();
            const key = slugKey(raw) || "result";
            return {label: `מ-${spec.label}`, outputs: [{key, label: raw || "תוצאת הנוסחה"}]};
        }
        return {label: `מ-${spec.label}`, outputs: spec.outputs ?? []};
    }
    return {label: "משלב", outputs: []};
}

function Canvas({seed, onRegisterGraph, companyId, onDirty}) {
    const [nodes, setNodes, onNodesChange] = useNodesState(seed?.nodes || initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(seed?.edges || initialEdges);
    const [selectedId, setSelectedId] = useState(null);
    const actionCountRef = useRef(0);
    const {screenToFlowPosition} = useReactFlow();

    // Keep a live ref to edges so the addToken() setNodes-updater can count a
    // parent's existing children without a stale closure.
    const edgesRef = useRef(edges);
    useEffect(() => { edgesRef.current = edges; }, [edges]);

    const selectedNode = useMemo(
        () => nodes.find(n => n.id === selectedId) || null,
        [nodes, selectedId]
    );

    // Build the "map from" groups for the selected node: walk edges BACKWARD to
    // collect every UPSTREAM brick's outputs (its ancestors), grouped by source
    // brick. Data flows forward along edges, so a brick can read the outputs of
    // anything before it — e.g. Send Email (after Create Trainee) can use
    // {{createTrainee.traineeId}}.
    const upstreamGroups = useMemo(() => {
        if (!selectedNode) return [];
        const byId = new Map(nodes.map(n => [n.id, n]));
        const parentOf = new Map(); // target -> source (one input per node)
        edges.forEach(e => parentOf.set(e.target, e.source));

        const groups = [];
        const seen = new Set();
        let cursor = parentOf.get(selectedNode.id);
        while (cursor && !seen.has(cursor)) {
            seen.add(cursor);
            const node = byId.get(cursor);
            if (node) {
                const {label, outputs} = describeNode(node);
                if (outputs.length) {
                    groups.push({
                        sourceId: node.id,
                        sourceLabel: label,
                        fields: outputs.map(o => ({
                            // A trigger's outputs are ALREADY fully-qualified paths (trainee.name,
                            // arbox.email) — use them as-is. Action/data bricks expose bare keys, so
                            // prefix those with the node's stable prefix (createTrainee_n5.traineeId).
                            path: node.type === "trigger" && o.path ? o.path : `${prefixFor(node)}.${o.key ?? o.path?.split(".").pop()}`,
                            label: o.label,
                        })),
                    });
                }
            }
            cursor = parentOf.get(cursor);
        }
        return groups; // nearest ancestor first
    }, [selectedNode, nodes, edges]);

    // Each node has exactly ONE input line: a new edge into a target replaces
    // any existing edge into that same target.
    const onConnect = useCallback(
        (params) => setEdges(eds => addEdge(
            {...params, animated: true, style: {stroke: BRAND}},
            eds.filter(e => e.target !== params.target),
        )),
        [setEdges]
    );

    const onNodeClick = useCallback((_e, node) => setSelectedId(node.id), []);
    const onPaneClick = useCallback(() => setSelectedId(null), []);

    // Expose the live graph to the parent toolbar (Save + server test-run) via a
    // ref-registered accessor, using live refs so the callback always sees the
    // current nodes/edges.
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);
    useEffect(() => {
        onRegisterGraph?.(() => ({nodes: nodesRef.current, edges: edgesRef.current}));
    }, [onRegisterGraph]);

    // Mark the workflow dirty on any node/edge change — but NOT on the first render (the seed load).
    const seededRef = useRef(false);
    useEffect(() => {
        if (!seededRef.current) { seededRef.current = true; return; }
        onDirty?.();
    }, [nodes, edges, onDirty]);

    // Remove an edge by id (the × button on a RemovableEdge calls this).
    const removeEdge = useCallback((edgeId) => {
        setEdges(eds => eds.filter(e => e.id !== edgeId));
    }, [setEdges]);

    // Remove a node and any edges touching it. Start + trigger are protected.
    const removeNode = useCallback((nodeId) => {
        if (nodeId === "start" || nodeId === "trigger") return;
        setNodes(nds => nds.filter(n => n.id !== nodeId));
        setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
        setSelectedId(prev => (prev === nodeId ? null : prev));
    }, [setNodes, setEdges]);

    // Inject the remove callback + type into every edge so the × button works.
    const renderedEdges = useMemo(
        () => edges.map(e => ({...e, type: "removable", data: {...e.data, onRemove: removeEdge}})),
        [edges, removeEdge]
    );

    const updateNodeData = useCallback((nodeId, newData) => {
        setNodes(nds => nds.map(n => (n.id === nodeId ? {...n, data: newData} : n)));
    }, [setNodes]);

    // Add a palette item (token = "nodeType:kind[:eventType]"). Triggers update
    // the single trigger node; data/action bricks are created + auto-connected.
    const addToken = useCallback((token, position) => {
        const {nodeType, data} = resolveToken(token);

        if (nodeType === "trigger") {
            // One trigger per flow — configure the existing trigger node.
            setNodes(nds => nds.map(n => (n.id === "trigger" ? {...n, data} : n)));
            setSelectedId("trigger");
            return;
        }

        const id = nextId();
        const idx = actionCountRef.current++;

        // Auto-connect from the currently selected node (fall back to trigger).
        // Topology defines execution: two edges out of one node = parallel;
        // a chain (A→B→C) = sequence. So selecting the trigger then adding fans
        // out (parallel); selecting an action then adding chains (sequence).
        setNodes(nds => {
            const parentId = (selectedId && nds.some(n => n.id === selectedId && n.type !== "start"))
                ? selectedId : "trigger";
            const parent = nds.find(n => n.id === parentId);
            // How many children already hang off this parent → stagger vertically.
            const siblings = edgesRef.current.filter(e => e.source === parentId).length;
            const base = parent ? parent.position : {x: 260, y: 180};
            const pos = position ?? {x: base.x + 210, y: base.y + siblings * 130};

            setEdges(eds => addEdge(
                {id: `e-${parentId}-${id}`, source: parentId, target: id, animated: true, style: {stroke: BRAND}},
                eds
            ));
            return [...nds, {id, type: nodeType, position: pos, data}];
        });
        setSelectedId(id);
    }, [setNodes, setEdges, selectedId]);

    // ── drag & drop from the palette ──────────────────────────────
    const onDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }, []);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        const token = e.dataTransfer.getData(DRAG_MIME);
        if (!token) return;
        const position = screenToFlowPosition({x: e.clientX, y: e.clientY});
        addToken(token, position);
    }, [screenToFlowPosition, addToken]);

    return (
        <Box sx={{display: "flex", height: "100%", minHeight: 0}}>
            {/* Left palette */}
            <BrickPalette/>

            {/* Canvas */}
            <Box sx={{flex: 1, minWidth: 0, position: "relative"}} onDragOver={onDragOver} onDrop={onDrop}>
                <ReactFlow
                    nodes={nodes}
                    edges={renderedEdges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    onPaneClick={onPaneClick}
                    onNodesDelete={(deleted) => deleted.forEach(n => removeNode(n.id))}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    deleteKeyCode={["Backspace", "Delete"]}
                    // Open at 1:1 near the top-right (RTL start). Icons are small,
                    // so no need to zoom out further.
                    defaultViewport={{x: 40, y: 60, zoom: 1}}
                    minZoom={0.4}
                    maxZoom={2}
                    proOptions={{hideAttribution: true}}
                >
                    <Background color={colors.border} gap={20}/>
                    <Controls showInteractive={false}/>
                </ReactFlow>
            </Box>

            {/* Right config panel */}
            {selectedNode && (
                <NodeConfigPanel
                    key={selectedNode.id}
                    node={selectedNode}
                    upstreamGroups={upstreamGroups}
                    companyId={companyId}
                    onChange={(newData) => updateNodeData(selectedNode.id, newData)}
                    onClose={() => setSelectedId(null)}
                    onDelete={selectedNode.id === "trigger" ? null : () => removeNode(selectedNode.id)}
                />
            )}
        </Box>
    );
}

export default function AutomationFlowPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const {notificationHandler} = useMessageContext();
    const {company} = useCompany();
    const {isSuperAdmin} = useAuth();

    const editId = params.get("id");
    const isEditing = Boolean(editId);

    // The flow being edited (name/enabled carried through to Save). Null when creating.
    const [flow, setFlow] = useState(null);
    // Unsaved-changes tracking: the Canvas signals onDirty on any node/edge change; saving/loading
    // resets it. Drives the Save-button indicator + the leave warnings.
    const [dirty, setDirty] = useState(false);
    const markDirty = useCallback(() => setDirty(true), []);

    // The company this builder operates on — drives the template/menu/form PICKERS (which must list
    // only THIS company's items) and the create request. Priority:
    //   1. When EDITING, the loaded flow's OWN company (a SYSTEM_ADMIN may have a different company
    //      selected in the top bar — the picker must still follow the automation's company, not the
    //      switcher). 2. Otherwise the top-bar company (SYSTEM_ADMIN creating a new flow).
    //   A regular admin is pinned server-side to their own company, so this stays undefined for them.
    const companyId = flow?.companyId
        ?? (isSuperAdmin() ? company?.id : undefined);
    // SYSTEM_ADMIN with "All Companies" selected has no target — block create with a clear message.
    const missingCompany = !isEditing && isSuperAdmin() && !company?.id;
    // Seed is resolved asynchronously (needs the server catalog for edit-mode). null until ready.
    const [seed, setSeed] = useState(null);
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load the server catalog (which triggers exist) + resolve the initial seed:
    //   ?id=…      → fetch the flow and rebuild the graph
    //   ?template= → seed from the template definition
    //   (neither)  → blank start+trigger
    useEffect(() => {
        let alive = true;
        (async () => {
            await loadCatalog();
            if (editId) {
                try {
                    const res = await getFlow(editId);
                    const loaded = res?.data;
                    if (alive && loaded) {
                        setFlow(loaded);
                        setSeed(deserializeFlow(loaded,
                            {triggerByKind, actionByKind, dataByKind: (k) => DATA_CATALOG.find(d => d.kind === k)},
                            BRAND));
                    }
                } catch (e) {
                    notificationHandler?.error?.("טעינת האוטומציה נכשלה");
                }
            } else {
                const tmpl = templateById(params.get("template"));
                setSeed(tmpl ? buildFromTemplate(tmpl) : {nodes: initialNodes, edges: initialEdges});
            }
            if (alive) setReady(true);
        })();
        return () => { alive = false; };
    }, [editId, params, notificationHandler]);

    // Canvas registers a live graph accessor; Save + Test-run read from it.
    const getGraphRef = useRef(null);
    const readGraph = () => getGraphRef.current?.();

    const goBack = () => {
        if (dirty && !window.confirm("יש שינויים שלא נשמרו באוטומציה. לצאת בלי לשמור?")) return;
        navigate("/app/automations-v2");
    };

    // Warn on browser-level navigation (refresh / close tab / back) while there are unsaved changes.
    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (!dirty) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const save = async () => {
        const graph = readGraph();
        if (!graph) return;
        if (missingCompany) {
            notificationHandler?.error?.("בחר חברה בסרגל העליון לפני יצירת אוטומציה");
            return;
        }
        const {ok, error, request} = serializeFlow(graph.nodes, graph.edges, {
            name: flow?.name || "אוטומציה חדשה",
            enabled: flow?.enabled ?? false,
        });
        if (!ok) {
            notificationHandler?.error?.(error);
            return;
        }
        setSaving(true);
        try {
            if (isEditing) {
                await updateFlow(editId, request);
                notificationHandler?.success?.("האוטומציה נשמרה");
            } else {
                await createFlow(request, companyId);
                notificationHandler?.success?.("האוטומציה נוצרה");
            }
            setDirty(false); // saved — no unsaved changes, don't warn on the navigate below
            navigate("/app/automations-v2");
        } catch (e) {
            notificationHandler?.error?.(e?.response?.data?.errorMessage || "שמירת האוטומציה נכשלה");
        } finally {
            setSaving(false);
        }
    };

    // Test run against the SERVER: serialize the current graph, save it (so there is a
    // flow id to run), then call the server test-run and show the per-step result.
    const [testResult, setTestResult] = useState(null);
    const [testOpen, setTestOpen] = useState(false);
    const [testing, setTesting] = useState(false);
    const runTest = async () => {
        const graph = readGraph();
        if (!graph) return;
        if (missingCompany) {
            notificationHandler?.error?.("בחר חברה בסרגל העליון לפני הרצת בדיקה");
            return;
        }
        const {ok, error, request} = serializeFlow(graph.nodes, graph.edges, {
            name: flow?.name || "אוטומציה חדשה", enabled: flow?.enabled ?? false,
        });
        if (!ok) {
            notificationHandler?.error?.(error);
            return;
        }
        setTesting(true);
        try {
            // Persist first (create or update) so the flow exists to run; the test-run
            // endpoint validates + executes without performing external side effects on stage.
            let id = editId;
            if (isEditing) {
                await updateFlow(editId, request);
            } else {
                const res = await createFlow(request, companyId);
                id = res?.data?.id;
                if (id) {
                    setFlow(res.data);
                    // switch the URL to edit mode so subsequent saves update the same flow
                    navigate(`/app/automations-flow?id=${id}`, {replace: true});
                }
            }
            const triggerData = sampleTriggerData(graph.nodes);
            const exec = await testRunFlow(id, triggerData);
            setTestResult(adaptExecutionToDrawer(exec?.data, graph.nodes));
            setTestOpen(true);
        } catch (e) {
            notificationHandler?.error?.(e?.response?.data?.errorMessage || "הרצת הבדיקה נכשלה");
        } finally {
            setTesting(false);
        }
    };

    // position:absolute + inset:0 pins the page to the layout's fixed-height
    // <main>, so the builder is a static frame — the page never scrolls, only the
    // canvas zooms and the palette scrolls internally when it's taller than the frame.
    return (
        <Box sx={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column", bgcolor: colors.bg, overflow: "hidden",
        }}>
            <RTL>
                <Box dir="rtl" sx={{
                    bgcolor: colors.white, borderBottom: borders.default,
                    px: 3, py: 1.5, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
                }}>
                    {/* Back + title */}
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                        <Button onClick={goBack} startIcon={<BackIcon/>} variant="text"
                                sx={{color: colors.textSecondary, minWidth: 0,
                                     "&:hover": {bgcolor: colors.bg, color: colors.textPrimary}}}>
                            חזרה
                        </Button>
                        <Box sx={{width: "1px", height: 28, bgcolor: colors.border}}/>
                        <BoltIcon sx={{color: colors.primary}}/>
                        <Box>
                            <Typography sx={{fontSize: "18px", fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2}}>
                                {isEditing ? "עריכת אוטומציה" : "בונה אוטומציות"}
                            </Typography>
                            <Typography sx={{fontSize: "12px", color: colors.textSecondary}}>
                                בחר טריגר וחבר אליו פעולות (נתונים לדוגמה בלבד)
                            </Typography>
                        </Box>
                    </Box>

                    {/* Test run + Save */}
                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <Button onClick={runTest} startIcon={<TestIcon/>} variant="outlined"
                                disabled={testing || !ready}
                                sx={{color: "#01BBA7", borderColor: "#01BBA7",
                                     "&:hover": {borderColor: "#019385", bgcolor: "#01BBA70D"}}}>
                            {testing ? "בודק…" : "הרצת בדיקה"}
                        </Button>
                        <Button onClick={save} startIcon={<SaveIcon/>} variant="contained"
                                disabled={saving || !ready}
                                sx={{bgcolor: "#01BBA7", "&:hover": {bgcolor: "#019385"}, boxShadow: "none"}}>
                            {saving ? "שומר…" : (isEditing ? "שמור שינויים" : "שמור")}
                            {dirty && !saving && (
                                <Box component="span" sx={{
                                    width: 8, height: 8, borderRadius: "50%", bgcolor: "#fff", ml: 1,
                                    display: "inline-block", flexShrink: 0,
                                }}/>
                            )}
                        </Button>
                    </Box>
                </Box>
            </RTL>
            {/* minHeight:0 lets this flex child shrink so its children own the overflow */}
            <Box sx={{flex: 1, minHeight: 0, overflow: "hidden"}}>
                {ready && (
                    <ReactFlowProvider>
                        <Canvas seed={seed} companyId={companyId} onDirty={markDirty} onRegisterGraph={(fn) => { getGraphRef.current = fn; }}/>
                    </ReactFlowProvider>
                )}
            </Box>

            <TestRunDrawer open={testOpen} result={testResult} onClose={() => setTestOpen(false)}/>
        </Box>
    );
}
